import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const Login = () => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [darkMode, setDarkMode] = useState(true);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const USER_API = process.env.REACT_APP_USER_SERVICE_URL;

  useEffect(() => {
    setForm({ email: "", password: "" });
    if (document.body.scrollHeight > window.innerHeight) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Safe JWT decode
  const decodeJWT = (token) => {
    try {
      const payload = token.split(".")[1];
      return JSON.parse(atob(payload));
    } catch (err) {
      console.error("Invalid JWT:", err);
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch(`${USER_API}/users/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      // Check for empty response first
      const text = await res.text();
      if (!text) throw new Error("Empty response from server");

      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error("Invalid JSON from server");
      }

      if (!res.ok) throw new Error(data.error || "Login failed");

      const decoded = decodeJWT(data.token);
      if (!decoded || decoded.role !== "BUYER") {
        throw new Error("You are not allowed to log in from this portal");
      }

      const authData = {
        userId: decoded.userId,
        email: decoded.sub,
        firstName: decoded.fullName?.split(" ")[0] || "",
        token: data.token,
        roles: [decoded.role],
      };

      login(authData);

      // Redirect buyer to buyer dashboard
      navigate("/buyer");
    } catch (err) {
      console.error("Login error:", err);
      setError(err.message);
      setForm((prev) => ({ ...prev, password: "" }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="d-flex justify-content-center align-items-center"
      style={{
        backgroundColor: darkMode ? "#121212" : "#f5f5f5",
        minHeight: "calc(100vh - 60px)",
        paddingTop: "60px",
        color: darkMode ? "#f5f5f5" : "#000",
      }}
    >
      <div
        className={`card shadow-sm p-4 ${
          darkMode ? "bg-dark text-light" : "bg-white"
        }`}
        style={{ width: "100%", maxWidth: "400px", borderRadius: "12px" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="text-center mb-0">Login</h3>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setDarkMode(!darkMode)}
          >
            {darkMode ? "Light Mode" : "Dark Mode"}
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label">Email</label>
            <input
              name="email"
              type="email"
              className="form-control"
              value={form.email}
              onChange={handleChange}
              required
              autoComplete="email"
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Password</label>
            <input
              name="password"
              type="password"
              className="form-control"
              value={form.password}
              onChange={handleChange}
              required
              autoComplete="current-password"
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary w-100"
            disabled={loading}
          >
            {loading ? "Logging in..." : "Login"}
          </button>

          {error && <div className="alert alert-danger mt-3">{error}</div>}
        </form>

        <div className="text-center mt-3">
          <Link to="/reset-password" className="btn btn-link p-0">
            <strong>Forgot Your Password?</strong>
          </Link>
        </div>
        <div className="text-center mt-3">
          <span>Don't have an account? </span>
          <Link to="/register" className="btn btn-link p-0">
            <strong>Register</strong>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
